import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHOP_CATEGORIES = ['Phone Accessories', 'Print', 'Soda', 'Juice', 'Snacks', 'Candy', 'Other'];

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');

  useEffect(() => {
    axios.get(`${API}/products?shop_only=true`).then((res) => setProducts(res.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Always show the main categories, plus any custom ones that exist among showcased products
  const productCategories = Array.from(new Set(products.map((p) => p.category)));
  const categories = ['all', ...SHOP_CATEGORIES.filter((c) => productCategories.includes(c) || SHOP_CATEGORIES.includes(c))];
  // Also include any custom categories not in the default list
  productCategories.forEach((c) => { if (!categories.includes(c)) categories.push(c); });
  const filtered = category === 'all' ? products : products.filter((p) => p.category === category);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white" data-testid="shop-page">
      {/* Top bar */}
      <div className="bg-white/5 backdrop-blur-md border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white font-semibold" data-testid="back-home-link"><i className="fas fa-arrow-left"></i> Back home</Link>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center"><i className="fas fa-store"></i></div>
            <span className="font-bold">Ashop Shop</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        <p className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">In-store now</p>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black leading-tight tracking-tight mb-4">
          Grab a snack.<br />
          <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">Charge your phone.</span>
        </h1>
        <p className="text-lg text-gray-300 max-w-2xl mb-8">Everything below is available in the shop right now. Visit us to grab yours.</p>

        {/* Category filter - always visible */}
        <div className="flex gap-2 flex-wrap mb-8" data-testid="shop-categories">
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${category === c ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`} data-testid={`shop-cat-${c}`}>
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-white/40"><i className="fas fa-spinner fa-spin text-3xl"></i></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10" data-testid="empty-shop">
            <i className="fas fa-store-slash text-5xl text-white/20 mb-3"></i>
            <p className="text-white/60 font-semibold">
              {category === 'all' ? 'Nothing here yet' : `No ${category} in stock right now`}
            </p>
            <p className="text-white/40 text-sm mt-1">Come back soon — we&apos;re restocking every week.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-grid">
            {filtered.map((p) => (
              <div key={p.id} className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 overflow-hidden hover:border-emerald-400/40 transition-all group" data-testid={`shop-product-${p.id}`}>
                {p.photo_url ? (
                  <div className="aspect-square bg-white/5 overflow-hidden"><img src={p.photo_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /></div>
                ) : (
                  <div className="aspect-square bg-gradient-to-br from-white/5 to-white/10 flex items-center justify-center"><i className="fas fa-image text-5xl text-white/20"></i></div>
                )}
                <div className="p-3">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-400/80 font-bold">{p.category}</p>
                  <p className="font-bold leading-tight mt-1">{p.name}</p>
                  {p.description && <p className="text-xs text-white/50 mt-1 line-clamp-2">{p.description}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xl font-black text-emerald-400">€{p.price.toFixed(2)}</span>
                    <span className={`text-[10px] font-bold ${p.stock <= 5 ? 'text-red-400' : 'text-white/50'}`}>
                      {p.stock > 0 ? (p.stock <= 5 ? `Only ${p.stock} left` : 'In stock') : 'Sold out'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-16 bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-cyan-500/20 border border-white/10 rounded-3xl p-8 text-center">
          <p className="text-2xl font-black mb-2">Visit us in Rotterdam</p>
          <p className="text-gray-300 mb-4">All items are available at the store. Cash or card accepted.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 font-bold rounded-2xl hover:bg-gray-100"><i className="fas fa-map-location-dot"></i> Get directions</Link>
        </div>
      </div>
    </div>
  );
};

export default Shop;
